/**
 * Resolve at five, publish at eight — Pass 06 §7.
 *
 * THE RESOLVE HAS NO JUDGEMENT IN IT. Given the choosing order, the Toilet Bowl lift, the
 * third-place declaration and the submitted preferences, there is exactly one correct answer and
 * `resolveDraftSlots` already produces it. Something with one right answer should not wait on a
 * human being awake, which is the whole argument for putting it on a schedule.
 *
 * THE THREE HOURS ARE THE RULEBOOK'S, NOT AN INVENTION. 2020-II-1 §5 puts submissions at 5:00 PM
 * and §7 puts publication at 8:00 PM the same evening. That gap is exactly the failure Tim named —
 * a GM whose ranking did not save the way he thought — so it is used as a cure window rather than
 * shortened to something tidier. A short window only buys a rushed correction; the answer is
 * already computed and sitting there.
 *
 *   open          before 5:00 PM. Rankings are being taken.
 *   provisional   resolved, commissioners only. Corrections re-run it.
 *   published     everybody sees the order. Automatic at 8:00 PM unless held.
 *   held          a commissioner stopped the clock, with a reason the league can read.
 *
 * AUTO-PUBLISH IS THE DEFAULT, NOT THE FALLBACK. The rule says published *by* 8:00 PM, so nobody
 * being at a keyboard must not make the league miss it. The two overrides are both deliberate acts
 * — publish early once satisfied, or hold with a stated reason — and a hold is visible rather than
 * silent, because a league staring at an unpublished order at 8:05 will assume the app is broken.
 *
 * RE-RUNNING IS SAFE BY CONSTRUCTION. Same inputs, same output; a correction is fix the preference,
 * re-resolve, provisional updates. There are no partial states to get stuck in.
 */

import { atHourEtOn, PUBLISH_HOUR_ET } from './deadlines.js';

export type PublicationState = 'open' | 'provisional' | 'held' | 'published';

export interface Hold {
  /** Stated, and shown to the league. A hold with no reason is a silence with extra steps. */
  reason: string;
  by: string;
  at: string;
}

export interface PublicationInput {
  /** 2020-II-1 §5 — when rankings lock and the resolve runs. */
  preferenceDeadline?: string | null;
  /** Set once the function has resolved. Its presence is what makes the result provisional. */
  resolvedAt?: string | null;
  /** Set once the order is the league's. */
  publishedAt?: string | null;
  /** A commissioner stopping the automatic publication. */
  hold?: Hold | null;
  now?: number;
}

const at = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

/** 8:00 PM ET on the day rankings were due — §7's deadline, derived from §5's. */
export const publishAt = (i: PublicationInput): string | null =>
  atHourEtOn(i.preferenceDeadline, PUBLISH_HOUR_ET);

export function publicationState(i: PublicationInput): PublicationState {
  if (i.publishedAt) return 'published';
  if (i.hold) return 'held';
  return i.resolvedAt ? 'provisional' : 'open';
}

/**
 * Whether the scheduled resolve should run now.
 *
 * IDEMPOTENT BY CONDITION, not by a lock. The job fires on a cron and may fire twice; asking
 * "has it been resolved" rather than "has the job run" means a second firing is a no-op and a
 * missed firing is caught by the next one. `force` is the correction path — a commissioner fixing
 * a preference during the window re-runs it on purpose.
 */
export function shouldResolve(i: PublicationInput, force = false): boolean {
  if (i.publishedAt) return false;
  const deadline = at(i.preferenceDeadline);
  if (deadline === null) return false;
  const now = i.now ?? Date.now();
  if (now < deadline) return false;
  return force || !i.resolvedAt;
}

/**
 * Whether the scheduled publication should fire now.
 *
 * It needs a resolve to publish, the hour to have come, and nobody to have held it. A hold blocks
 * this and only a commissioner clearing the hold un-blocks it — deliberately, because a hold that
 * expired on its own would publish exactly the disputed order it was raised over.
 */
export function shouldPublish(i: PublicationInput): boolean {
  if (i.publishedAt || i.hold) return false;
  if (!i.resolvedAt) return false;
  const due = at(publishAt(i));
  if (due === null) return false;
  return (i.now ?? Date.now()) >= due;
}

/** The cure window: resolved, not yet published, and the clock has not run out. */
export function inCureWindow(i: PublicationInput): boolean {
  if (publicationState(i) !== 'provisional') return false;
  const due = at(publishAt(i));
  return due === null ? true : (i.now ?? Date.now()) < due;
}

/**
 * WHO MAY SEE THE RESOLVED ORDER.
 *
 * Commissioners during the window, everybody after publication. A GM seeing his slot at 5:01 and
 * the league seeing it at 8:00 would make the cure window a private advantage rather than a
 * safety net — and a correction that moved somebody's slot after he had been told it would be
 * the first genuine dispute this app produced.
 */
export const orderVisibleTo = (i: PublicationInput, isCommissioner: boolean): boolean =>
  publicationState(i) === 'published' || isCommissioner;

/* ----------------------------------------------------------------------- the audit snapshot */

/**
 * WHAT THE RESOLVE READ, frozen beside what it produced.
 *
 * The lottery seed already makes the DRAW replayable. This makes the RESOLUTION replayable, which
 * is what an argument in September is actually about — not "was the draw fair" but "why did I get
 * the 7th slot when I ranked it fourth". Every input, with each preference's own `submittedAt`, so
 * a ranking saved at 4:59 can be told apart from one saved at 5:02.
 *
 * Stored rather than re-derived because the inputs change: a correction during the window rewrites
 * a preference, and without the snapshot the evidence for the first resolve is gone.
 */
export interface ResolutionSnapshot {
  /** When the resolve ran. */
  at: string;
  /** The choosing order it walked, AFTER the Toilet Bowl lift and any third-place swap. */
  order: string[];
  /** Before the swap, so the two can be compared. */
  drawnOrder: string[];
  toiletBowlWinner: string | null;
  thirdPlace: string | null;
  fourthPlace: string | null;
  /** What the third-place option ended up as, and how it was declared. */
  swap: { state: string; declaredVia?: string | null; recordedBy?: string | null } | null;
  /** Every submitted ranking with the stamp on it. */
  preferences: Array<{ gm: string; ranked: number[]; submittedAt: string | null }>;
  /** The answer. GM → first-round slot. */
  slots: Array<{ gm: string; slot: number }>;
  /** A correction made during the cure window, recorded against the snapshot it replaced. */
  corrections?: Array<{ gm: string; by: string; at: string; note: string }>;
}

/**
 * A one-line account of a resolution, for the commissioner's card and for the record.
 *
 * Deliberately says how many rankings it had rather than how many it wanted: "resolved on 7 of 10
 * rankings" is the sentence that makes somebody check, and §6 means the other three were not
 * ignored — they took the earliest slot left.
 */
export function resolutionLine(s: ResolutionSnapshot | null | undefined, teams: number): string {
  if (!s) return 'Not resolved yet.';
  const submitted = s.preferences.filter((p) => p.submittedAt !== null).length;
  const swap = s.swap?.state === 'taken' ? ', with the third-place swap taken' : '';
  return `Resolved on ${submitted} of ${teams} submitted rankings${swap}.`;
}
